import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, BookOpen, Scale, Calendar, ArrowRight, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

export const SUGGESTIONS = [
  "My landlord is asking me to vacate",
  "How to file FIR?",
  "Divorce process in India",
  "Property dispute help",
];

export function EmptyState({ onSuggest }) {
  return (
                  <div className="max-w-xl mx-auto text-center py-12">
                    <div className="h-12 w-12 mx-auto rounded-2xl bg-[#DBEAFE] text-[#1E3A8A] grid place-items-center">
                      <Sparkles size={22} />
                    </div>
                    <h2 className="mt-4 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>How can I help?</h2>
                    <p className="mt-2 text-sm text-slate-600">Ask anything about Indian law. Try one of these:</p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-2">
                      {SUGGESTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => onSuggest(s)}
                          data-testid={`suggestion-${s.slice(0, 10).replace(/\s+/g, "-")}`}
                          className="text-left px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-[#1E3A8A] hover:bg-blue-50/50 text-sm text-slate-700 transition-all"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
  );
}

export function AssistantMessage({ m }) {
  return (
                      <div className="max-w-[92%] bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                        {m.summary ? (
                          <div className="space-y-3">
                            <div>
                              <p className="text-[10px] font-bold tracking-widest text-[#1E3A8A] uppercase flex items-center gap-1">
                                <BookOpen size={10} /> Summary
                              </p>
                              <p className="text-sm text-slate-800 mt-1">{m.summary}</p>
                            </div>
                            {m.explanation && (
                              <div>
                                <p className="text-[10px] font-bold tracking-widest text-[#1E3A8A] uppercase">Legal Explanation</p>
                                <p className="text-sm text-slate-800 mt-1 leading-relaxed whitespace-pre-wrap">{m.explanation}</p>
                              </div>
                            )}
                            {m.next_steps?.length > 0 && (
                              <div>
                                <p className="text-[10px] font-bold tracking-widest text-[#1E3A8A] uppercase">Next Steps</p>
                                <ul className="mt-1 space-y-1.5">
                                  {m.next_steps.map((s, i) => (
                                    <li key={`${s}-${i}`} className="flex gap-2 text-sm text-slate-800">
                                      <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" /> {s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                        )}
                      </div>
  );
}

export function ChatMessage({ m }) {
  return (
    <div data-testid={`message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
      {m.role === "user" ? (
        <div className="max-w-[85%] bg-blue-50 text-slate-900 rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
          {m.content}
        </div>
      ) : <AssistantMessage m={m} />}
    </div>
  );
}

export function TypingIndicator() {
  return (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2" data-testid="typing-indicator">
                      <span className="h-2 w-2 rounded-full bg-[#1E3A8A] animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-2 w-2 rounded-full bg-[#1E3A8A] animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-2 w-2 rounded-full bg-[#1E3A8A] animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
  );
}

// Conversion prompt after 1-2 AI responses — nudge to paid consultation
export function LawyerConversionPrompt() {
  return (
                  <div
                    data-testid="lawyer-conversion-prompt"
                    className="relative rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#FBF4DF] to-white p-4 md:p-5 mt-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl adv-navy grid place-items-center shrink-0">
                        <Scale size={18} className="text-[#D4AF37]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
                          Want expert advice from a verified lawyer?
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          Real lawyers, real answers — matched to your city & language.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            asChild
                            data-testid="prompt-chat-with-lawyer"
                            className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4"
                          >
                            <Link to="/lawyers?type=chat">
                              <MessageSquare size={13} /> Chat with Lawyer
                            </Link>
                          </Button>
                          <Button
                            asChild
                            variant="outline"
                            data-testid="prompt-book-consultation"
                            className="rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold text-xs h-9 px-4"
                          >
                            <Link to="/lawyers">
                              <Calendar size={13} /> Book Consultation <ArrowRight size={13} />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
  );
}
